/** @file DefaultsSection: Settings > Defaults: New puppet scope and profile, and the export options. */
import type { ReactElement } from 'react';
import type { ExportDefaults, NewPuppetDefaults } from '../shell/editorSettings';
import { EDITOR_SETTINGS } from '../shell/editorSettings';
import { useSetting } from '../shell/useSetting';
import { Field } from '../ui-kit/Field';
import { SettingNumber } from '../ui-kit/SettingNumber';
import { Panel } from '../ui-kit/Panel';
import type { SelectOption } from '../ui-kit/Select';
import { Select } from '../ui-kit/Select';

const NONE: SelectOption = { value: '', label: 'No default (choose each time)' };
const DIRECTIONS: SelectOption[] = [NONE, ...[1, 2, 4, 8, 16].map((n) => ({ value: String(n), label: `${String(n)} direction${n === 1 ? '' : 's'}` }))];
const PITCH: SelectOption[] = [NONE, { value: 'none', label: 'none' }, { value: 'basic', label: 'basic' }, { value: 'full', label: 'full' }];
const CAMERA: SelectOption[] = [NONE, { value: 'iso', label: 'iso' }, { value: 'side', label: 'side' }];
const PROFILE: SelectOption[] = [NONE, { value: 'pixel16', label: 'pixel16' }, { value: 'hd', label: 'hd' }];
const BAKE_DIRECTIONS: SelectOption[] = [{ value: '', label: "Puppet's facings" }, ...DIRECTIONS.slice(1)];
const FORMATS: SelectOption[] = [{ value: 'bundle', label: 'Runtime bundle' }, { value: 'baked', label: 'Baked sprite sheets' }];

/** The text of a stored choice (null = none). */
const text = (v: string | number | null): string => (v === null ? '' : String(v));

/** The New puppet defaults: each row preselects a choice in the modal; "No default" leaves it for the human. */
function NewPuppetDefaultsRows(): ReactElement {
  const [d, setD] = useSetting(EDITOR_SETTINGS.newPuppet);
  const set = (patch: Partial<NewPuppetDefaults>): void => { setD({ ...d, ...patch }); };
  const directions = (v: string): void => { set({ directions: v === '' ? null : (Number(v) as NewPuppetDefaults['directions']) }); };
  const pitch = (v: string): void => { set({ pitch: v === '' ? null : (v as NewPuppetDefaults['pitch']) }); };
  const camera = (v: string): void => { set({ camera: v === '' ? null : (v as NewPuppetDefaults['camera']) }); };
  const profile = (v: string): void => { set({ profile: v === '' ? null : (v as NewPuppetDefaults['profile']) }); };
  return (
    <>
      <Field label="New puppet: directions"><Select label="Default directions" value={text(d.directions)} options={DIRECTIONS} onChange={directions} /></Field>
      <Field label="New puppet: head pitch"><Select label="Default head pitch" value={text(d.pitch)} options={PITCH} onChange={pitch} /></Field>
      <Field label="New puppet: camera"><Select label="Default camera" value={text(d.camera)} options={CAMERA} onChange={camera} /></Field>
      <Field label="New puppet: profile"><Select label="Default profile" value={text(d.profile)} options={PROFILE} onChange={profile} /></Field>
    </>
  );
}

/** The export defaults the Export popover and File > Export start from. */
function ExportDefaultsRows(): ReactElement {
  const [d, setD] = useSetting(EDITOR_SETTINGS.export);
  const set = (patch: Partial<ExportDefaults>): void => { setD({ ...d, ...patch }); };
  return (
    <>
      <Field label="Export: default format">
        <Select label="Default export format" value={d.format} options={FORMATS} onChange={(format) => { set({ format: format === 'baked' ? 'baked' : 'bundle' }); }} />
      </Field>
      <Field label="Export: baked directions">
        <Select label="Default bake directions" value={d.directions} options={BAKE_DIRECTIONS} onChange={(directions) => { set({ directions }); }} />
      </Field>
      <Field label="Export: baked scale (1..8)">
        <SettingNumber label="Default bake scale" value={d.scale} min={1} max={8} integer onCommit={(scale) => { set({ scale }); }} />
      </Field>
    </>
  );
}

/** Defaults a human would otherwise pick again each time; agents are unaffected (they always say what they want). */
export function DefaultsSection(): ReactElement {
  return (
    <Panel title="Defaults">
      <div className="p3-stack" style={{ padding: 12 }}>
        <NewPuppetDefaultsRows />
        <ExportDefaultsRows />
      </div>
    </Panel>
  );
}
